import { useState } from "react";
import { HeaderButton } from "../design-system/Button/HeaderButton";
import { UserMinus2, UserPlus2 } from "@tamagui/lucide-icons";
import { ArrowPathIcon } from "react-native-heroicons/solid";
import { useAtomValue } from "jotai";
import { currentUserAtom } from "../../../atoms/global";
import { useToast } from "../../hooks/useToast";
import { useApiQuery, useApiPost, useApiPatch, api } from "../../../lib/api";

type FriendshipStatus = "pending" | "accepted" | null;

export type Friendship = {
  id: string;
  user_id: string;
  friend_id: string;
  status: FriendshipStatus;
};

type FriendshipResponse = {
  friendship: Friendship | null;
};

type Props = {
  userId?: string;
};

export const FriendshipButton = ({ userId }: Props) => {
  const toast = useToast();
  const currentUser = useAtomValue(currentUserAtom);
  const currentUserId = currentUser?.id;

  const { data, isLoading: isFetching, refetch } = useApiQuery<FriendshipResponse>(
    ["friendship", currentUserId, userId],
    `/api/v1/follows?userId=${userId}`,
    { enabled: !!currentUserId && !!userId }
  );

  const [localFriendship, setLocalFriendship] = useState<Friendship | null | undefined>(undefined);
  const friendship = localFriendship !== undefined ? localFriendship : data?.friendship ?? null;

  const { mutate: sendRequest, isPending: isSending } = useApiPost<
    FriendshipResponse,
    { friend_id: string }
  >("/api/v1/follows", {
    onSuccess: (res) => {
      setLocalFriendship(res.friendship);
      toast({ title: "Friend request sent", haptic: "success" });
    },
  });

  const { mutate: acceptRequest, isPending: isAccepting } = useApiPatch<
    FriendshipResponse,
    { friendship_id: string }
  >("/api/v1/follows", {
    onSuccess: (res) => {
      setLocalFriendship(res.friendship);
      toast({ title: "Friend Request Accepted", haptic: "success" });
    },
  });

  const [isRemoving, setIsRemoving] = useState(false);

  const handlePress = async () => {
    if (!userId) return;

    if (friendship?.status === "accepted") {
      setIsRemoving(true);
      try {
        await api.delete(`/api/v1/follows?friendshipId=${friendship.id}`);
        setLocalFriendship(null);
      } finally {
        setIsRemoving(false);
      }
    } else if (friendship?.user_id && friendship.user_id !== currentUserId) {
      acceptRequest({ friendship_id: friendship.id });
    } else {
      sendRequest({ friend_id: userId });
    }
  };

  const isLoading = isFetching || isSending || isAccepting || isRemoving;

  const friendshipStatus = friendship?.status;

  return (
    <HeaderButton onPress={handlePress} disabled={isLoading}>
      {isLoading ? (
        <ArrowPathIcon color="black" width={16} height={16} />
      ) : friendshipStatus === "accepted" ||
        (friendship?.user_id === currentUser?.id &&
          friendshipStatus === "pending") ? (
        <UserMinus2 color="black" width={16} height={16} />
      ) : (
        <UserPlus2 color="black" width={16} height={16} />
      )}
    </HeaderButton>
  );
};
